import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useI18n } from '../../contexts/I18nContext';
import { useSubscriptionAccess } from '../../contexts/SubscriptionAccessContext';
import { useBrand } from '../../../brands/contexts/BrandContext';
import {
  getActiveSubscriptionOrNull,
  getActiveTariffs,
  type AdminTariff,
  type BillingPeriod,
} from '../../utils/subscriptionApiUtils';
import { protocolLimitLabel, tariffMonthlyEquivalent, tariffPriceLabel } from './subscriptionFormatters';
import './SubscriptionPages.scss';

const TariffSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useI18n();
  const { brand } = useBrand();
  const { mustPurchaseSubscription } = useSubscriptionAccess();
  const [tariffs, setTariffs] = useState<AdminTariff[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [currentTariffId, setCurrentTariffId] = useState<number | null>(null);

  const logoUrl = `${process.env.PUBLIC_URL}/${brand.logos.small}`;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, sub] = await Promise.all([getActiveTariffs(), getActiveSubscriptionOrNull()]);
        if (cancelled) return;
        setTariffs(list.filter((x) => x.is_active));
        if (sub?.subscription?.tariff_id) {
          setCurrentTariffId(sub.subscription.tariff_id);
        }
      } catch (e) {
        if (!cancelled) {
          enqueueSnackbar(e instanceof Error ? e.message : t('subscription.loadError'), { variant: 'error' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enqueueSnackbar, t]);

  const selectTariff = (tariff: AdminTariff) => {
    navigate(`/subscription/checkout?tariff_id=${tariff.id}&period=${period}`);
  };

  if (loading) {
    return (
      <div className="subscription-page">
        <div className="subscription-page__loader">{t('subscription.loading')}</div>
      </div>
    );
  }

  return (
    <div className="subscription-page subscription-page--tariffs">
      <header
        className={`subscription-page__header${mustPurchaseSubscription ? ' subscription-page__header--solo' : ''}`}
      >
        {!mustPurchaseSubscription && (
          <button type="button" className="subscription-page__back" onClick={() => navigate('/subscription')} aria-label="Back">
            ←
          </button>
        )}
        <h1 className="subscription-page__title">{t('subscription.selectTariffTitle')}</h1>
      </header>

      <div className="subscription-tariffs-toolbar">
        <p className="subscription-tariffs-toolbar__hint">{t('subscription.tariffsToolbarHint')}</p>
        <div className="subscription-period-toggle" role="tablist">
          <button
            type="button"
            className={period === 'monthly' ? 'active' : ''}
            onClick={() => setPeriod('monthly')}
          >
            {t('subscription.periodMonthly')}
          </button>
          <button
            type="button"
            className={period === 'yearly' ? 'active' : ''}
            onClick={() => setPeriod('yearly')}
          >
            {t('subscription.periodYearly')}
          </button>
        </div>
      </div>

      <div className="subscription-tariff-list">
        {tariffs.map((tariff) => {
          const isCurrent = currentTariffId === tariff.id;
          return (
            <button
              key={tariff.id}
              type="button"
              className={`subscription-tariff-card ${isCurrent ? 'subscription-tariff-card--current' : ''}`}
              onClick={() => selectTariff(tariff)}
            >
              {isCurrent && <span className="subscription-tariff-card__ribbon">{t('subscription.currentBadge')}</span>}
              <img src={logoUrl} alt="" className="subscription-tariff-card__logo" />
              <div className="subscription-tariff-card__name">{tariff.name}</div>
              <div className="subscription-tariff-card__price">{tariffPriceLabel(tariff, period)}</div>
              <div className="subscription-tariff-card__eq">{tariffMonthlyEquivalent(tariff, period)}</div>
              <p className="subscription-tariff-card__feat">{protocolLimitLabel(tariff)}</p>
              {tariff.description && (
                <p className="subscription-tariff-card__desc">{tariff.description}</p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TariffSelectionPage;
